<template>
  <div class="sbs-cols">
    <div class="sbs-toolbar">
      <p class="sbs-toolbar__hint">
        <b>{{ year }}-yil</b> jadvalining ustunlari. Yangi ustun shu yilga biriktiriladi;
        yonma-yon bir xil guruhli ustunlar sarlavhada birlashadi.
      </p>
      <el-button type="primary" @click="openCreate">
        <span class="sbs-btn-plus">+</span> {{ year }}-yilga ustun qo'shish
      </el-button>
    </div>

    <el-skeleton v-if="loading" :rows="6" animated />
    <MfEmpty v-else-if="!columns.length" :title="`${year}-yil uchun ustun yo'q`"
      description="Yuqoridagi tugma orqali birinchi ustunni biriktiring" />
    <div v-else class="sbs-table-wrap">
      <el-table :data="columns" class="sbs-table" :row-class-name="rowClass">
        <el-table-column label="Tartib" width="80" align="center">
          <template #default="scope">
            <span class="sbs-order-chip">{{ scope.row.order }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Guruh" min-width="160">
          <template #default="scope">
            <span v-if="scope.row.group_code" class="sbs-grp-tag">{{ displayLangText(scope.row.group) ||
              scope.row.group_code }}</span>
            <span v-else class="sbs-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Sarlavha" min-width="200">
          <template #default="scope">
            <span class="sbs-col-title">{{ displayLangText(scope.row.title) || "..." }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Birlik" min-width="140">
          <template #default="scope">
            {{ displayLangText(scope.row.unit) || (scope.row.value_type === "AMOUNT" ? scaleUnitLabel(scope.row.scale) :
            "") }}
          </template>
        </el-table-column>
        <el-table-column label="Turi" width="100" align="center">
          <template #default="scope">
            <el-tag size="small" :type="typeTagType(scope.row.value_type)" effect="light">
              {{ valueTypeLabel(scope.row.value_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Biriktirilgan" width="140" align="center">
          <template #default="scope">
            <el-tag v-if="scope.row.year" size="small" type="success" effect="light"
              :title="`Faqat ${scope.row.year}-yil jadvalida ko'rinadi`">
              {{ scope.row.year }}-yil
            </el-tag>
            <el-tag v-else size="small" type="info" effect="plain" title="Barcha yillar jadvalida ko'rinadi">
              Hamma yillar
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Holati" width="150" align="center">
          <template #default="scope">
            <el-switch :model-value="scope.row.is_active !== false" :loading="togglingId === scope.row.id"
              style="--el-switch-on-color: #16A34A" @change="onToggleActive(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="Amallar" width="200" align="center">
          <template #default="scope">
            <el-space :size="6">
              <el-button size="small" title="Yuqoriga" :disabled="isFirst(scope.row)"
                @click="move(scope.row, -1)">↑</el-button>
              <el-button size="small" title="Pastga" :disabled="isLast(scope.row)"
                @click="move(scope.row, 1)">↓</el-button>
              <el-button type="primary" size="small" title="Tahrirlash" @click="openEdit(scope.row)">
                <img height="15" src="@/assets/images/icons/edit.svg" alt="Tahrirlash" />
              </el-button>
              <el-button type="danger" size="small" title="O'chirish" :loading="deletingId === scope.row.id"
                @click="onDelete(scope.row)">
                <img height="15" src="@/assets/images/icons/delete.svg" alt="O'chirish" />
              </el-button>
            </el-space>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <SubsidiesColumnDialog v-model="dialogVisible" :column="editingColumn" :columns="columns"
      :group-options="groupOptions" :default-year="year" @saved="onSaved" />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import MfEmpty from "@/components/core/MfEmpty.vue";
import SubsidiesColumnDialog from "./SubsidiesColumnDialog.vue";
import { API_BASE, displayLangText, valueTypeLabel, scaleUnitLabel } from "./subsidiesTypes";

export default {
  name: "SubsidiesColumns",
  components: { MfEmpty, SubsidiesColumnDialog },
  props: {
    year: { type: Number, required: true },
  },
  emits: ["changed"],
  data() {
    return {
      columns: [],
      loading: false,
      dialogVisible: false,
      editingColumn: null,
      togglingId: null,
      deletingId: null,
      movingId: null,
    };
  },
  computed: {
    groupOptions() {
      const map = new Map();
      this.columns.forEach((c) => {
        if (c.group_code && !map.has(c.group_code)) {
          map.set(c.group_code, { group_code: c.group_code, group: c.group });
        }
      });
      return Array.from(map.values());
    },
  },
  methods: {
    displayLangText,
    valueTypeLabel,
    scaleUnitLabel,
    typeTagType(type) {
      if (type === "AMOUNT") return "primary";
      if (type === "PERCENT") return "success";
      return "info";
    },
    rowClass({ row }) {
      return row.is_active === false ? "sbs-row--inactive" : "";
    },
    handleApiError(error, fallbackTitle) {
      ElNotification({
        title: fallbackTitle,
        message: error?.response?.data?.message || "Iltimos, qayta urinib ko'ring.",
        type: "error",
      });
    },
    columnPayload(column, overrides = {}) {
      return {
        group_code: column.group_code || "",
        group: column.group,
        title: column.title,
        unit: column.unit,
        value_type: column.value_type,
        scale: column.scale,
        decimals: column.decimals,
        year: column.year ?? null,
        order: column.order,
        is_active: column.is_active !== false,
        ...overrides,
      };
    },
    fetchColumns() {
      this.loading = true;
      this.$http
        .get(`${API_BASE}/columns`, { params: { year: this.year, include_inactive: true } })
        .then(({ data }) => {
          this.columns = Array.isArray(data) ? data : [];
        })
        .catch((error) => this.handleApiError(error, "Ustunlarni yuklashda xatolik"))
        .finally(() => {
          this.loading = false;
        });
    },
    onSaved() {
      this.fetchColumns();
      this.$emit("changed");
    },
    openCreate() {
      this.editingColumn = null;
      this.dialogVisible = true;
    },
    openEdit(column) {
      this.editingColumn = column;
      this.dialogVisible = true;
    },
    isFirst(row) {
      return this.columns.indexOf(row) === 0;
    },
    isLast(row) {
      return this.columns.indexOf(row) === this.columns.length - 1;
    },
    move(row, dir) {
      const idx = this.columns.indexOf(row);
      const otherIdx = idx + dir;
      if (otherIdx < 0 || otherIdx >= this.columns.length) return;
      const other = this.columns[otherIdx];
      this.movingId = row.id;
      const putRow = (c, order) => this.$http.put(`${API_BASE}/columns/${c.id}`, this.columnPayload(c, { order }));
      Promise.all([putRow(row, other.order), putRow(other, row.order)])
        .then(() => {
          this.fetchColumns();
        })
        .catch((error) => this.handleApiError(error, "Tartibni o'zgartirishda xatolik"))
        .finally(() => {
          this.movingId = null;
        });
    },
    onToggleActive(column) {
      this.togglingId = column.id;
      this.$http
        .put(`${API_BASE}/columns/${column.id}`, this.columnPayload(column, { is_active: !(column.is_active !== false) }))
        .then(() => {
          ElMessage({ type: "success", message: "Holati yangilandi" });
        })
        .catch((error) => this.handleApiError(error, "Holatni o'zgartirishda xatolik"))
        .finally(() => {
          this.togglingId = null;
          this.onSaved();
        });
    },
    onDelete(column) {
      // "Hamma yillar" ustuni bitta yildan emas, butun modulda o'chadi — buni aytib qo'yamiz.
      const scope = column.year
        ? `Bu ustun ${column.year}-yil jadvalidan olib tashlanadi.`
        : "Bu ustun \"Hamma yillar\" ustuni — u BARCHA yillar jadvalidan olib tashlanadi.";
      ElMessageBox.confirm(
        `${scope} Tiklash imkoni yo'q — kiritilgan qiymatlar bazada qoladi, lekin hech qayerda ko'rinmaydi.`,
        "Ustunni o'chirish",
        { confirmButtonText: "Ha, o'chirish", cancelButtonText: "Bekor qilish", type: "warning" },
      )
        .then(() => {
          this.deletingId = column.id;
          this.$http
            .delete(`${API_BASE}/columns/${column.id}`)
            .then(() => {
              ElMessage({ type: "success", message: "Ustun o'chirildi" });
              this.onSaved();
            })
            .catch((error) => this.handleApiError(error, "O'chirishda xatolik"))
            .finally(() => {
              this.deletingId = null;
            });
        })
        .catch(() => {
        });
    },
  },
  watch: {
    year() {
      this.fetchColumns();
    },
  },
  mounted() {
    this.fetchColumns();
  },
};
</script>

<style scoped lang="scss">
$brand: #007791;
$navy: #0a194e;
$border: #ebeef5;
$muted: #8a94a6;

.sbs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  &__hint {
    margin: 0;
    font-size: 13px;
    color: $muted;
  }
}

.sbs-btn-plus {
  font-size: 16px;
  margin-right: 2px;
}

.sbs-table-wrap {
  border: 1px solid $border;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(15, 30, 80, 0.04);
}

.sbs-table {
  width: 100%;

  :deep(.el-table-fixed-column--right) {
    background-color: #fff !important;
    z-index: 3 !important;
  }

  :deep(th.el-table-fixed-column--right) {
    background-color: #f6f9fc !important;
  }

  :deep(th.el-table__cell) {
    background: #f6f9fc;
    color: $navy;
    font-family: "Montserrat-Medium", sans-serif;
    font-weight: 600;
    height: 50px;
  }

  :deep(.sbs-row--inactive) {
    opacity: 0.55;
  }
}

.sbs-order-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 24px;
  padding: 0 7px;
  border-radius: 7px;
  background: #eef1f6;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.sbs-grp-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #f4f6fa;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
}

.sbs-col-title {
  font-weight: 600;
  color: #303133;
}

.sbs-muted {
  color: $muted;
}

</style>
